import { Component, Input, OnInit } from '@angular/core';
import { ControlContainer, FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, tap } from 'rxjs/operators';

import { AvailableCountry, City } from 'models';
import { ExecService } from 'services';
import * as FormConstants from '../../../shared/constants/form.constants';

@Component({
  selector: 'sb-form-address',
  templateUrl: './form-address.component.html'
})
export class FormAddressComponent implements OnInit {
  private static USER_INPUT_DEBOUNCE_INTERVAL = 200;

  public addressFormGroup: FormGroup;
  public FormConstants = FormConstants;
  public cityAutocompleteInputLoading = false;
  public cityAutocompleteInputDisabled = false;
  public cityAutocompleteInputOptions: City[] = [];
  public searchCitiesOnUserInputSubject = new Subject();
  public searchZipOnUserInputSubject = new Subject();
  @Input() public countriesAutocompleteInputOptions: AvailableCountry[];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private controlContainer: ControlContainer,
    private execService: ExecService
  ) {}

  ngOnInit(): void {
    this.addressFormGroup = this.controlContainer.control as FormGroup;
    this.configureAutocompleteFields(this.addressFormGroup);
  }

  get shouldShowRecipient() {
    return this.addressFormGroup && this.addressFormGroup.get('recipient');
  }

  get shouldShowCountries() {
    return this.addressFormGroup && this.addressFormGroup.get('country');
  }

  get shouldShowAtField() {
    return this.addressFormGroup && this.addressFormGroup.get('at');
  }

  public fillDistrictFromCityModel(city: City) {
    const districtFormControl = this.addressFormGroup.get('district');
    if (!districtFormControl) {
      return;
    }

    districtFormControl.patchValue(city.district);
  }

  private searchCitiesOnUserInput(cityValue: string) {
    this.cityAutocompleteInputOptions = [];
    this.cityAutocompleteInputLoading = true;

    this.execService.getCitiesFromCityName(cityValue)
      .pipe(
        first(Boolean),
        tap(() => (this.cityAutocompleteInputLoading = false))
      )
      .subscribe((citiesFound: City[]) => {
        this.cityAutocompleteInputOptions = citiesFound;
      });
  }

  private configureAutocompleteFields(addressFormGroup: FormGroup) {
    if (!addressFormGroup) {
      return;
    }

    this.configureZipAutocomplete(addressFormGroup);
    this.configureCityAutocomplete(addressFormGroup);
  }

  private configureZipAutocomplete(addressFormGroup: FormGroup): void {
    const zipCodeFormControl = addressFormGroup.get('zipCode');
    if (!zipCodeFormControl) {
      return;
    }

    this.subscriptions.add(
      this.searchZipOnUserInputSubject
        .pipe(
          debounceTime(FormAddressComponent.USER_INPUT_DEBOUNCE_INTERVAL),
          filter((zipCodeValue: string) => (zipCodeValue || '').length === FormConstants.MAXIMUM_ZIP_CODE_LENGTH),
          distinctUntilChanged()
        )
        .subscribe((zipCodeValue) => {
          this.fillCityFormControlFromZipCode(zipCodeValue);
        })
    );
  }

  private configureCityAutocomplete(addressFormGroup: FormGroup): void {
    // Listen to user-input changes
    this.searchCitiesOnUserInputSubject
      .pipe(
        debounceTime(FormAddressComponent.USER_INPUT_DEBOUNCE_INTERVAL),
        filter((cityValue: string) => ((cityValue || '').length >= FormConstants.MINIMUM_CITY_LENGTH)),
        distinctUntilChanged(),
      )
      .subscribe((cityValue: string) => {
        this.searchCitiesOnUserInput(cityValue);
      });


    // Listen to control value changes (form value patches)
    const cityFormControl = addressFormGroup.get('city');
    const districtFormControl = addressFormGroup.get('district');
    if (!cityFormControl || !districtFormControl) {
      return;
    }

    this.subscriptions.add(
      cityFormControl.valueChanges
        .pipe(
          filter(Boolean),
          filter((cityValue: string) => ((cityValue || '').length >= FormConstants.MINIMUM_CITY_LENGTH)),
          distinctUntilChanged()
        )
        .subscribe((cityValue) => {
          const foundCityIndex = this.cityAutocompleteInputOptions.findIndex((city: City) => city.city === cityValue);

          if (foundCityIndex === -1) {
            const cityFormControlValue = {
              fullName: cityValue,
              city: cityValue
            } as City;

            this.cityAutocompleteInputOptions = [ cityFormControlValue ];
          } else {
            districtFormControl.patchValue(this.cityAutocompleteInputOptions[foundCityIndex].district);
          }
        })
    );
  }

  private fillCityFormControlFromZipCode(zipCodeValue) {
    const cityFormControl = this.addressFormGroup.get('city');
    const districtFormControl = this.addressFormGroup.get('district');
    if (!cityFormControl || !districtFormControl) {
      return;
    }

    this.cityAutocompleteInputLoading = true;
    this.cityAutocompleteInputDisabled = true;

    this.execService
      .getCityNamesFromZipCode(zipCodeValue)
      .pipe(
        first(Boolean),
        tap(() => {
          this.cityAutocompleteInputLoading = false;
          this.cityAutocompleteInputDisabled = false;
        })
      )
      .subscribe((citiesFromZipCode: City[]) => {
        this.cityAutocompleteInputOptions = citiesFromZipCode;
        // Fractions are uppercase. We filter them
        const citiesWithoutFractions = citiesFromZipCode.filter(cityVal => cityVal.city.toUpperCase() !== cityVal.city);
        // We should filter fractions. BUT if all results are fractions, we show them all.

        if (citiesWithoutFractions.length) {
          this.cityAutocompleteInputOptions = citiesWithoutFractions;
        }

        if (this.cityAutocompleteInputOptions.length === 1) {
          const cityFullModel: City = this.cityAutocompleteInputOptions[0];
          cityFormControl.patchValue(cityFullModel.city);
          districtFormControl.patchValue(cityFullModel.district);
        } else {
          cityFormControl.patchValue(null, { emitEvent: false });
          districtFormControl.patchValue(null, { emitEvent: false });
        }
      });
  }
}
